import { Alert, AlertDescription, AlertIcon, Button, VStack, Text, Box } from '@chakra-ui/react'
import { isEmpty } from 'lodash'
import { FunctionComponent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../../app/typedReduxHooks'
import {
  PrescriberClient,
  UpdatePrescriberRequest,
} from '../../NewScriptPage/ScriptForm/PrescriberSlice'
import { Prescriber } from '../../../types'
import ProfileForm from './ProfileForm'
import { STATUS } from '../../../constants'
import { LocationProfilesList } from '../../../features/locations/components/LocationProfilesList'
import { useHasEntitlement } from '../../OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../../../types/organization-entitlements'

const ProfilePage: FunctionComponent<unknown> = () => {
  const { prescriber } = useAppSelector((state) => state.prescriber)
  const hasMultipleLocations = useHasEntitlement(FeatureKey.MULTIPLE_LOCATIONS)
  const [updateStatus, setUpdateStatus] = useState(STATUS.DEFAULT)
  const [disabledSubmit, setDisabledSubmit] = useState(false)

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    setError,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: prescriber as Prescriber,
  })

  const dispatch = useAppDispatch()

  const updatePrescriber = async (data: UpdatePrescriberRequest) => {
    setUpdateStatus(STATUS.PENDING)
    const result = (await dispatch(PrescriberClient.UpdatePrescriber(data))) as any
    if (result.meta.requestStatus === 'fulfilled') {
      setUpdateStatus(STATUS.SUCCESS)
    }

    if (result.meta.requestStatus === 'rejected') {
      setUpdateStatus(STATUS.FAILURE)
    }
  }

  useEffect(() => {
    reset(prescriber as Prescriber)
    setUpdateStatus(STATUS.DEFAULT)
  }, [reset, prescriber])

  return (
    <Box w="100%" pb={10}>
      <VStack
        as="form"
        alignItems="flex-start"
        w="100%"
        spacing="6"
        onSubmit={handleSubmit(updatePrescriber)}
        aria-label="Prescriber Profile Form"
      >
        <ProfileForm
          {...{
            register,
            getValues,
            setValue,
            errors,
            setDisabledSubmit,
            prescriber,
            setError,
            clearErrors,
          }}
        />
        <Box w="100%">
          <Button
            colorScheme="blue"
            type="submit"
            size="md"
            px={8}
            disabled={disabledSubmit || isSubmitting || !isEmpty(errors)}
            isLoading={isSubmitting}
          >
            Update
          </Button>
          {!isEmpty(errors) && (
            <Text fontSize="xs" color="red.600" mt={2}>
              The update button will be enabled when all validation passes
            </Text>
          )}

          {[STATUS.SUCCESS, STATUS.FAILURE].includes(updateStatus) && (
            <Alert
              status={updateStatus === STATUS.FAILURE ? 'error' : 'success'}
              mt={4}
              width="fit-content"
            >
              <AlertIcon />
              <AlertDescription fontSize="sm">
                {updateStatus === STATUS.FAILURE
                  ? 'Sorry, failed to update your profile.'
                  : 'Successfully updated your profile'}
              </AlertDescription>
            </Alert>
          )}
        </Box>
      </VStack>

      {hasMultipleLocations && (
        <Box mt={6} w="100%">
          <LocationProfilesList />
        </Box>
      )}
    </Box>
  )
}

export default ProfilePage
